import type {
  CreateInstitutionPayload,
  Institution,
  InstitutionInvoice,
  InstitutionPaymentMethod,
  InstitutionStatsResponse,
  InstitutionSubscription,
  UpdateInstitutionPayload,
} from '../../types/index.js';
import { baseApi } from '../base-api.js';

type InstitutionResponse = { institution: Institution };
type InstitutionMutationResponse = { institution: Institution; message: string };
type CreateInstitutionResponse = {
  institution: Institution;
  access_token: string;
  refresh_token: string;
  expires_in: number;
  message: string;
};
type CreateInstitutionResult = Omit<CreateInstitutionResponse, 'message'>;
export type ResumeInstitutionSubscriptionResult =
  | { status: 'active'; message: string }
  | { status: 'processing' }
  | { status: 'requires_action'; client_secret: string };

/**
 * Mirrors `apps/server/src/modules/institution` (`institutionController`, prefix `/institutions`)
 * — the owner/practitioner-facing institution profile + billing routes. Admin routes
 * (`GET /admin/list`) are out of scope, covered by the separate admin lot.
 *
 * All tags use the fixed `Institution` type with singleton ids (`SELF`/`STATS`/`SUBSCRIPTION`/
 * `PAYMENT_METHOD`/`INVOICES`) rather than per-entity ids — there is exactly one institution per
 * owner and each route returns a different projection of it (or of its Stripe state), not a list.
 * `createInstitution`'s `access_token`/`refresh_token` are kept in the response as-is (unlike the
 * `message` field, stripped everywhere) since the app needs them to update its token storage
 * after the owner's JWT gains an `institution_id` claim — that's an app-level side effect
 * (`onQueryStarted` or similar), not something this layer can or should do.
 */
export const institutionApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getInstitution: builder.query<Institution, void>({
      query: () => ({ path: ['institutions'], method: 'get' }),
      transformResponse: (response: InstitutionResponse) => response.institution,
      providesTags: [{ type: 'Institution', id: 'SELF' }],
    }),

    createInstitution: builder.mutation<CreateInstitutionResult, CreateInstitutionPayload>({
      query: (body) => ({ path: ['institutions'], method: 'post', body }),
      transformResponse: ({ institution, access_token, refresh_token, expires_in }: CreateInstitutionResponse) => ({
        institution,
        access_token,
        refresh_token,
        expires_in,
      }),
      invalidatesTags: [{ type: 'Institution', id: 'SELF' }],
    }),

    updateInstitution: builder.mutation<Institution, UpdateInstitutionPayload>({
      query: (body) => ({ path: ['institutions'], method: 'put', body }),
      transformResponse: (response: InstitutionMutationResponse) => response.institution,
      invalidatesTags: [{ type: 'Institution', id: 'SELF' }],
    }),

    getInstitutionStats: builder.query<InstitutionStatsResponse, void>({
      query: () => ({ path: ['institutions', 'stats'], method: 'get' }),
      transformResponse: (response: { stats: InstitutionStatsResponse }) => response.stats,
      providesTags: [{ type: 'Institution', id: 'STATS' }],
    }),

    getInstitutionSubscription: builder.query<InstitutionSubscription, void>({
      query: () => ({ path: ['institutions', 'subscription'], method: 'get' }),
      transformResponse: (response: { subscription: InstitutionSubscription }) => response.subscription,
      providesTags: [{ type: 'Institution', id: 'SUBSCRIPTION' }],
    }),

    getInstitutionPaymentMethod: builder.query<InstitutionPaymentMethod | null, void>({
      query: () => ({ path: ['institutions', 'payment-method'], method: 'get' }),
      transformResponse: (response: { payment_method: InstitutionPaymentMethod | null }) => response.payment_method,
      providesTags: [{ type: 'Institution', id: 'PAYMENT_METHOD' }],
    }),

    createInstitutionSetupIntent: builder.mutation<string | null, void>({
      query: () => ({ path: ['institutions', 'setup-intent'], method: 'post' }),
      transformResponse: (response: { client_secret: string | null }) => response.client_secret,
    }),

    updateInstitutionPaymentMethod: builder.mutation<void, string>({
      query: (paymentMethodId) => ({
        path: ['institutions', 'update-payment-method'],
        method: 'post',
        body: { payment_method_id: paymentMethodId },
      }),
      transformResponse: (): void => undefined,
      invalidatesTags: [{ type: 'Institution', id: 'PAYMENT_METHOD' }],
    }),

    getInstitutionInvoices: builder.query<InstitutionInvoice[], void>({
      query: () => ({ path: ['institutions', 'invoices'], method: 'get' }),
      transformResponse: (response: { invoices: InstitutionInvoice[] }) => response.invoices,
      providesTags: [{ type: 'Institution', id: 'INVOICES' }],
    }),

    resumeInstitutionSubscription: builder.mutation<ResumeInstitutionSubscriptionResult, void>({
      query: () => ({ path: ['institutions', 'resume-subscription'], method: 'post' }),
      invalidatesTags: [
        { type: 'Institution', id: 'SELF' },
        { type: 'Institution', id: 'SUBSCRIPTION' },
        { type: 'Institution', id: 'STATS' },
      ],
    }),

    cancelInstitutionSubscription: builder.mutation<void, void>({
      query: () => ({ path: ['institutions', 'cancel-subscription'], method: 'post' }),
      transformResponse: (): void => undefined,
      invalidatesTags: [
        { type: 'Institution', id: 'SELF' },
        { type: 'Institution', id: 'SUBSCRIPTION' },
        { type: 'Institution', id: 'STATS' },
      ],
    }),

    reactivateInstitutionSubscription: builder.mutation<void, void>({
      query: () => ({ path: ['institutions', 'reactivate-subscription'], method: 'post' }),
      transformResponse: (): void => undefined,
      invalidatesTags: [
        { type: 'Institution', id: 'SELF' },
        { type: 'Institution', id: 'SUBSCRIPTION' },
        { type: 'Institution', id: 'STATS' },
      ],
    }),
  }),
});

export const {
  useGetInstitutionQuery,
  useCreateInstitutionMutation,
  useUpdateInstitutionMutation,
  useGetInstitutionStatsQuery,
  useGetInstitutionSubscriptionQuery,
  useGetInstitutionPaymentMethodQuery,
  useCreateInstitutionSetupIntentMutation,
  useUpdateInstitutionPaymentMethodMutation,
  useGetInstitutionInvoicesQuery,
  useResumeInstitutionSubscriptionMutation,
  useCancelInstitutionSubscriptionMutation,
  useReactivateInstitutionSubscriptionMutation,
} = institutionApi;
